import {
  applySession,
  dueSkills,
  Economy,
  newSkillState,
  type AttemptOutcome,
  type SkillState as CoreSkillState,
} from '@audigoal/core';
import type { CharacterId } from '@audigoal/core';
import { db, getLocalProfile, lessonProgressKey, putLessonProgress, putSkillStates } from './db';
import { localDate } from './daily';
import { enqueue } from './sync-queue';
import {
  addBondPoints,
  applyAchievementProgress,
  applyQuestProgress,
  sectionReached,
  todaysQuests,
} from './gamification';
import { awardCertificate } from '@/features/certificate/certificate';
import type { AchievementRow, QuestRow } from './types';
import type { Attempt, LessonProgress, SessionRecord, SkillState as SkillStateRow } from './types';

/**
 * The bridge between core's learning engine and the local store.
 *
 * Core owns an FSRS card; the row that is stored (and mirrored to Supabase)
 * flattens it. These two functions are the only place that translation happens,
 * so a field added to the card has exactly one place to be remembered.
 */

export function rowToState(row: SkillStateRow): CoreSkillState {
  return {
    skillId: row.skillId,
    mastery: row.mastery,
    streak: row.streak,
    lastDifficulty: row.lastDifficulty,
    seen: row.seen,
    introduced: row.introduced,
    card: {
      due: row.dueAt === null ? new Date() : new Date(row.dueAt),
      stability: row.stability,
      difficulty: row.difficulty,
      elapsed_days: row.elapsedDays,
      scheduled_days: row.scheduledDays,
      learning_steps: row.learningSteps,
      reps: row.reps,
      lapses: row.lapses,
      state: row.fsrsState,
      ...(row.lastReview === null ? {} : { last_review: new Date(row.lastReview) }),
    },
  };
}

export function stateToRow(state: CoreSkillState, now = new Date()): SkillStateRow {
  return {
    skillId: state.skillId,
    mastery: state.mastery,
    fsrsState: state.card.state,
    stability: state.card.stability,
    difficulty: state.card.difficulty,
    dueAt: state.card.due.toISOString(),
    lastReview: state.card.last_review?.toISOString() ?? null,
    elapsedDays: state.card.elapsed_days,
    scheduledDays: state.card.scheduled_days,
    learningSteps: state.card.learning_steps,
    reps: state.card.reps,
    lapses: state.card.lapses,
    streak: state.streak,
    lastDifficulty: state.lastDifficulty,
    seen: state.seen,
    introduced: state.introduced,
    updatedAt: now.toISOString(),
    dirty: true,
  };
}

/** Every skill state the learner has, keyed by skill id. */
export async function loadSkillStates(): Promise<Map<string, CoreSkillState>> {
  const rows = await db.skillStates.toArray();
  return new Map(rows.map((row) => [row.skillId, rowToState(row)]));
}

export interface SessionOutcome {
  sessionId: string;
  kind: SessionRecord['kind'];
  refId: string | null;
  instrument: string | null;
  crownLevel: number | null;
  seed: number;
  startedAt: string;
  endedAt: string;
  attempts: {
    templateId: string;
    skillIds: string[];
    seed: number;
    difficulty: number;
    paramsHash: string;
    correct: boolean;
    score: number;
    responseMs: number;
    usedFallback: boolean;
    answeredAt: string;
  }[];
  exercises: number;
  correct: number;
  mistakes: number;
  maxCombo: number;
  accuracy: number;
  perfect: boolean;
  xpEarned: number;
  contentVersion: number;
  /** Songs: the stars earned, and whether this was the first completion. */
  stars?: number;
  firstCompletion?: boolean;
  /**
   * The path node to crown when the session is not itself a lesson: a song
   * node played from the path names its lesson here, since `refId` is the song.
   */
  crownLessonId?: string;
  /** Extra facts the gamification counters need, beyond what XP uses. */
  micPasses?: number;
  earExercises?: number;
  earCorrect?: number;
  skillsReviewed?: number;
  studioTool?: string;
  practiceMode?: string;
  onSecondInstrument?: boolean;
  legendary?: boolean;
  chordChanges?: number;
  longTones?: number;
  speedRoundScore?: number;
  /** True when this session finished the last lesson of a unit. */
  unitCompleted?: boolean;
  /** The character hosting this lesson, for their bond. */
  host?: CharacterId;
  /** Hosts of the questions answered correctly, for a mixed practice run. */
  questionHosts?: CharacterId[];
}

export interface AppliedSession {
  /** Bond points this session earned, by character. */
  bonds: Partial<Record<CharacterId, number>>;
  /** Skill states before and after, for the completion screen. */
  skillDeltas: Record<string, { before: number; after: number }>;
  crowns: number;
  earnedCrown: boolean;
  /** Quests this session moved, for the completion screen's "almost there". */
  questsMoved: QuestRow[];
  /** Achievements that reached a new tier. */
  achievementsTiered: AchievementRow[];
  /** True when this session was the Master's Certificate exam, and passed it. */
  certificateEarned: boolean;
}

/**
 * Records a finished session: the attempts, the session row, the new skill
 * states from the learning engine, and the lesson's crown count. Everything is
 * written locally first and queued for the backend, so the UI never waits.
 */
export async function applySessionResult(outcome: SessionOutcome): Promise<AppliedSession> {
  const now = new Date();
  const existing = await loadSkillStates();

  // Group attempts by skill: the engine rates a skill once per session.
  const bySkill = new Map<string, AttemptOutcome[]>();
  for (const attempt of outcome.attempts) {
    for (const skillId of attempt.skillIds) {
      const list = bySkill.get(skillId) ?? [];
      list.push({
        score: attempt.score,
        responseMs: attempt.responseMs,
        difficulty: attempt.difficulty,
        hinted: attempt.usedFallback,
      });
      bySkill.set(skillId, list);
    }
  }

  const skillDeltas: Record<string, { before: number; after: number }> = {};
  const updated: SkillStateRow[] = [];
  for (const [skillId, outcomes] of bySkill) {
    const before = existing.get(skillId) ?? newSkillState(skillId, now);
    const after = applySession(before, outcomes, { now });
    skillDeltas[skillId] = { before: before.mastery, after: after.mastery };
    updated.push(stateToRow({ ...after, introduced: true }, now));
  }

  const attempts: Attempt[] = outcome.attempts.map((a) => ({
    sessionId: outcome.sessionId,
    templateId: a.templateId,
    skillIds: a.skillIds,
    seed: a.seed,
    difficulty: a.difficulty,
    paramsHash: a.paramsHash,
    correct: a.correct,
    score: a.score,
    responseMs: a.responseMs,
    usedFallback: a.usedFallback,
    answeredAt: a.answeredAt,
  }));

  const session: SessionRecord = {
    id: outcome.sessionId,
    kind: outcome.kind,
    refId: outcome.refId,
    instrument: outcome.instrument,
    crownLevel: outcome.crownLevel,
    seed: outcome.seed,
    startedAt: outcome.startedAt,
    endedAt: outcome.endedAt,
    exercises: outcome.exercises,
    correct: outcome.correct,
    mistakes: outcome.mistakes,
    maxCombo: outcome.maxCombo,
    accuracy: outcome.accuracy,
    xpEarned: outcome.xpEarned,
    gemsEarned: 0,
    perfect: outcome.perfect,
    contentVersion: outcome.contentVersion,
    synced: false,
  };

  // Crowns only advance for a path node that met the accuracy bar. Lessons,
  // checkpoints and listening lessons are their own node; a song played from
  // the path says which node sent it.
  let crowns = 0;
  let earnedCrown = false;
  let progressRow: LessonProgress | null = null;
  const nodeId =
    outcome.crownLessonId ??
    (outcome.kind === 'lesson' || outcome.kind === 'checkpoint' || outcome.kind === 'listening'
      ? outcome.refId
      : null);
  if (nodeId !== null) {
    const key = lessonProgressKey(nodeId, outcome.instrument ?? '');
    const current = await db.lessonProgress.get(key);
    const before = current?.crowns ?? 0;
    earnedCrown = Economy.earnsCrown(outcome.accuracy, before);
    crowns = Math.min(5, before + (earnedCrown ? 1 : 0));
    const row: LessonProgress = {
      key,
      lessonId: nodeId,
      instrument: outcome.instrument ?? '',
      crowns,
      legendary: current?.legendary ?? false,
      timesCompleted: (current?.timesCompleted ?? 0) + 1,
      bestAccuracy: Math.max(current?.bestAccuracy ?? 0, outcome.accuracy),
      lastCompletedAt: outcome.endedAt,
      updatedAt: now.toISOString(),
      dirty: true,
    };
    await putLessonProgress(row);
    progressRow = row;
  }

  await db.transaction('rw', db.sessions, db.attempts, db.skillStates, async () => {
    await db.sessions.put(session);
    if (attempts.length > 0) await db.attempts.bulkAdd(attempts);
    if (updated.length > 0) await putSkillStates(updated);
  });

  // Quests and achievements. Both are computed here rather than on the server
  // alone so the completion screen can show them at once and offline; the
  // server recomputes from the same facts and its answer wins.
  const profile = await getLocalProfile();
  const questsMoved: QuestRow[] = [];
  const achievementsTiered: AchievementRow[] = [];
  let questSet: { quest_id: string; difficulty: string }[] = [];
  let questProgress: Partial<Record<Economy.QuestMetric, number>> = {};
  let achievementProgress: Partial<Record<Economy.AchievementMetric, number>> = {};

  if (profile !== undefined) {
    const issued = await todaysQuests(profile, now);
    questSet = issued.map((row) => ({ quest_id: row.questId, difficulty: row.difficulty }));

    const facts = {
      kind: outcome.kind,
      exercises: outcome.exercises,
      correct: outcome.correct,
      mistakes: outcome.mistakes,
      maxCombo: outcome.maxCombo,
      accuracy: outcome.accuracy,
      perfect: outcome.perfect,
      xpEarned: outcome.xpEarned,
      endedHour: new Date(outcome.endedAt).getHours(),
      ...(outcome.micPasses !== undefined ? { micPasses: outcome.micPasses } : {}),
      ...(outcome.earExercises !== undefined ? { earExercises: outcome.earExercises } : {}),
      ...(outcome.skillsReviewed !== undefined ? { skillsReviewed: outcome.skillsReviewed } : {}),
      ...(outcome.studioTool !== undefined ? { studioTool: outcome.studioTool } : {}),
      ...(outcome.practiceMode !== undefined ? { practiceMode: outcome.practiceMode } : {}),
      ...(outcome.stars !== undefined ? { stars: outcome.stars } : {}),
      ...(outcome.legendary !== undefined ? { legendary: outcome.legendary } : {}),
      ...(outcome.onSecondInstrument !== undefined
        ? { onSecondInstrument: outcome.onSecondInstrument }
        : {}),
      minutes: Math.max(
        0,
        Math.round(
          (new Date(outcome.endedAt).getTime() - new Date(outcome.startedAt).getTime()) / 60000,
        ),
      ),
    };

    questProgress = Economy.questProgress(facts);
    questsMoved.push(...(await applyQuestProgress(questProgress, now)));

    achievementProgress = Economy.achievementProgress({
      kind: outcome.kind,
      perfect: outcome.perfect,
      endedHour: facts.endedHour,
      ...(outcome.legendary !== undefined ? { legendary: outcome.legendary } : {}),
      ...(outcome.micPasses !== undefined ? { micPasses: outcome.micPasses } : {}),
      ...(outcome.earCorrect !== undefined ? { earCorrect: outcome.earCorrect } : {}),
      ...(outcome.stars !== undefined ? { stars: outcome.stars } : {}),
      ...(outcome.chordChanges !== undefined ? { chordChanges: outcome.chordChanges } : {}),
      ...(outcome.longTones !== undefined ? { longTones: outcome.longTones } : {}),
      ...(outcome.speedRoundScore !== undefined
        ? { speedRoundScore: outcome.speedRoundScore }
        : {}),
      lessonsToday: await lessonsToday(now),
    });
    achievementsTiered.push(...(await applyAchievementProgress(achievementProgress, profile, now)));
  }

  // Bonds. A point only ever goes to the character whose domain earned it, so
  // a mixed practice run splits between the hosts of its questions rather than
  // handing the lot to whoever happened to front the session.
  const bonds = Economy.bondPoints({
    host: outcome.host ?? null,
    correct: outcome.correct,
    ...(outcome.questionHosts !== undefined ? { questionHosts: outcome.questionHosts } : {}),
    ...(outcome.micPasses !== undefined ? { micPasses: outcome.micPasses } : {}),
    lessonCompleted: outcome.kind === 'lesson' || outcome.kind === 'checkpoint',
    ...(outcome.crownLevel !== null ? { crownReached: crowns } : {}),
    ...(outcome.legendary !== undefined ? { legendary: outcome.legendary } : {}),
  });
  await addBondPoints(bonds);

  // One queue item, because the server applies all of it in one transaction
  // (plan doc 03). Sending the skill states and the crown separately would put
  // three ordered items in the queue for what is a single atomic result.
  // The server recomputes XP and Picks; the local figures are only a preview.
  await enqueue('session_result', {
    session,
    attempts,
    skillStates: updated,
    lessonProgress: progressRow,
    ...(outcome.stars !== undefined ? { stars: outcome.stars } : {}),
    ...(outcome.firstCompletion !== undefined ? { firstCompletion: outcome.firstCompletion } : {}),
    section_reached: profile === undefined ? 1 : sectionReached(profile),
    quest_set: questSet,
    quest_progress: questProgress,
    achievement_progress: achievementProgress,
    ...(outcome.unitCompleted === true ? { unit_completed: true } : {}),
    ...(Object.keys(bonds).length > 0 ? { bond_points: bonds } : {}),
  });

  // The Master's Certificate: the last node of Section 10, passed at 85%.
  let certificateEarned = false;
  if (outcome.refId === Economy.CERTIFICATE_LESSON_ID) {
    certificateEarned = await awardCertificate({
      correct: outcome.correct,
      total: Math.max(1, outcome.exercises),
      awardedAt: outcome.endedAt,
      name: profile?.displayName ?? profile?.username ?? '',
    });
  }

  return {
    skillDeltas,
    crowns,
    earnedCrown,
    questsMoved,
    achievementsTiered,
    bonds,
    certificateEarned,
  };
}

/**
 * Lessons finished today, for the Marathoner achievement.
 *
 * Counted from the session rows rather than kept as a running total, because a
 * counter that has to be reset at local midnight is a counter that will one day
 * be reset on the wrong day.
 */
async function lessonsToday(now: Date): Promise<number> {
  const today = localDate(now);
  const sessions = await db.sessions.toArray();
  return sessions.filter(
    (s) =>
      (s.kind === 'lesson' || s.kind === 'checkpoint') && localDate(new Date(s.endedAt)) === today,
  ).length;
}

/** Crowns per lesson id for the active instrument, for the path. */
export async function loadLessonProgress(instrument: string): Promise<Map<string, LessonProgress>> {
  const rows = await db.lessonProgress.toArray();
  const out = new Map<string, LessonProgress>();
  for (const row of rows) {
    // A theory lesson is stored with an empty instrument; a track lesson with its own.
    if (row.instrument === '' || row.instrument === instrument) out.set(row.lessonId, row);
  }
  return out;
}

/** Skills due for review right now, for the practice hub's badge. */
export async function dueSkillCount(now = new Date()): Promise<number> {
  const states = [...(await loadSkillStates()).values()];
  return dueSkills(states, now).length;
}

export interface PlacementRecord {
  /** Highest section cleared; 0 means the learner starts at the beginning. */
  result: number;
  /** Checkpoint lesson ids to mark as passed, which unlocks what is below. */
  checkpointLessonIds: string[];
  skills: { skillId: string; mastery: number; dueAt: Date }[];
  instrument: string;
}

/**
 * Writes the outcome of the placement test.
 *
 * Placed-out skills are not "learned": they get a mastery and a review date so
 * they surface again soon, but no review history, because none happened. An
 * existing state is never overwritten — a learner retaking the test should not
 * lose real progress to an estimate.
 */
export async function applyPlacementResult(
  record: PlacementRecord,
  now = new Date(),
): Promise<{ skillsMarked: number; checkpointsPassed: number }> {
  const existing = new Set((await db.skillStates.toArray()).map((row) => row.skillId));
  const rows: SkillStateRow[] = [];

  for (const { skillId, mastery, dueAt } of record.skills) {
    if (existing.has(skillId)) continue;
    const state = newSkillState(skillId);
    rows.push({
      ...stateToRow({ ...state, mastery, card: { ...state.card, due: dueAt } }, now),
      seen: 0,
      introduced: true,
    });
  }

  /**
   * A node granted this way gets its first crown, and never fewer than it
   * already has.
   *
   * Placement used to write `crowns: 1` flat. That was harmless while this
   * only ran at sign-up, when there is nothing to overwrite. Testing out of a
   * section runs it on a learner mid-course, and a flat write would take
   * someone with three crowns on a lesson back down to one — real practice
   * replaced by an estimate, which is the one thing this function is careful
   * not to do to skill states.
   */
  const existingProgress = new Map(
    (await db.lessonProgress.toArray()).map((row) => [row.key, row]),
  );
  const progress: LessonProgress[] = record.checkpointLessonIds.map((lessonId) => {
    const key = lessonProgressKey(lessonId, '');
    const held = existingProgress.get(key);
    return {
      key,
      lessonId,
      instrument: '',
      crowns: Math.max(1, held?.crowns ?? 0),
      legendary: held?.legendary ?? false,
      timesCompleted: held?.timesCompleted ?? 0,
      bestAccuracy: held?.bestAccuracy ?? null,
      lastCompletedAt: held?.lastCompletedAt ?? now.toISOString(),
      updatedAt: now.toISOString(),
      dirty: true,
    };
  });

  await db.transaction('rw', [db.skillStates, db.lessonProgress], async () => {
    if (rows.length > 0) await putSkillStates(rows);
    for (const row of progress) await putLessonProgress(row);
  });

  if (rows.length > 0) await enqueue('skill_states', rows);
  if (progress.length > 0) await enqueue('lesson_progress', progress);

  return { skillsMarked: rows.length, checkpointsPassed: progress.length };
}

/** Sessions that count as lessons for the "after your second lesson" rules. */
const LESSON_KINDS = new Set<SessionRecord['kind']>(['lesson', 'checkpoint']);

/**
 * How many lessons this device has finished. Both the home-screen card and
 * the consent prompt wait for it, so the two rules cannot drift apart.
 */
export async function completedLessonCount(): Promise<number> {
  return db.sessions.filter((session) => LESSON_KINDS.has(session.kind)).count();
}
